/* eslint-disable react-native/no-inline-styles */
/*
Author: Rendra Pramulia
*/

import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';

import * as Layout from '@components/screen/Layout';
import {
  SociollaInput,
  SociollaFileImageList,
  SociollaSelectBox,
} from '@components/sociolla/form';
import SociollaText from '@components/sociolla/SociollaText';

import Colors from '@global/colors';
import { getAllStates, getAllCities, getAllDistricts } from '@api/ms-account';
import { isWeb, isMobileWeb } from '@global/screens';
import PropTypes from 'prop-types';
import { PAYMENT_TYPES, PKP_TYPES, WHT } from '@/constants';
import { defaultCountry, isVn, checkDetailAddress } from '@global/helpers';

const propTypes = {
  data: PropTypes.object,
};

function CustomerInfo(props) {
  const [country, setCountry] = useState();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState('');
  const [districts, setDistricts] = useState([]);
  const [formEdit, setFormEdit] = useState({
    state: false,
    city: false,
    district: false,
  });

  const countries = useSelector((state) => state.resources.countries);
  const { setFieldValue, values } = useFormikContext();

  const onChangeState = async (selectedState) => {
    await setFieldValue('city', '', true);
    await setFieldValue('district', '', true);
    await setFieldValue('postal_code', '');
    setDistricts([]);
    getAllCities({
      params: {
        filter: { state_id: selectedState._id },
        sort: JSON.stringify({ type: 1, name: 1 }),
      },
    }).then((res) => {
      if (city) {
        const selectedCity = checkDetailAddress(res.data.data, city);
        if (selectedCity) {
          setFieldValue('city', selectedCity);
        }
      }
      setCity('');
      setCities(res.data.data);
      setFormEdit((prev) => ({ ...prev, city: true }));
    });
  };

  const onChangeCity = async (selectedCity) => {
    await setFieldValue('district', '', true);
    await setFieldValue('postal_code', '');
    if (isVn()) {
      return;
    }
    getAllDistricts({
      params: {
        filter: { city_id: selectedCity._id || selectedCity.id },
        sort: JSON.stringify({ name: 1 }),
      },
    }).then((res) => {
      setDistricts(res.data.data);
      setFormEdit((prev) => ({ ...prev, district: true }));
    });
  };

  const onChangeDistrict = async () => {
    await setFieldValue('postal_code', '');
  };

  useEffect(() => {
    let isMounted = true;
    if (countries?.length && !states.length) {
      const cn = countries.find(
        (x) => x.name.toLowerCase() === defaultCountry(),
      );
      getAllStates({
        params: {
          filter: { country_id: cn._id },
          sort: JSON.stringify({ name: 1 }),
        },
      }).then((res) => {
        if (isMounted) {
          setCountry(cn);
          setStates(res.data.data);
          setFormEdit((prev) => ({ ...prev, state: true }));
        }
      });
      if (props.data?.state?.id || props.data?.state?._id) {
        getAllCities({
          params: {
            filter: { state_id: props.data.state.id || props.data.state._id },
            sort: JSON.stringify({ type: 1, name: 1 }),
          },
        }).then((res) => {
          if (isMounted) {
            setCities(res.data.data);
            setFormEdit((prev) => ({ ...prev, city: true }));
          }
        });
      }
      if ((props.data?.city?.id || props.data?.city?._id) && !isVn()) {
        getAllDistricts({
          params: {
            filter: { city_id: props.data.city.id || props.data.city._id },
            sort: JSON.stringify({ name: 1 }),
          },
        }).then((res) => {
          if (!isMounted) {
            return;
          }
          const fetchedDistricts = res.data.data || [];
          setDistricts(fetchedDistricts);
          setFormEdit((prev) => ({ ...prev, district: true }));

          const existingDistrictId =
            props.data?.district?.id || props.data?.district?._id;
          if (existingDistrictId) {
            const matched = fetchedDistricts.find(
              (d) => d._id === existingDistrictId,
            );
            if (matched) {
              setFieldValue('district', matched);
            }
            return;
          }

          if (props.data?.postal_code) {
            const candidates = fetchedDistricts.filter((d) =>
              (d.pin_codes || []).includes(String(props.data.postal_code)),
            );
            if (candidates.length === 1) {
              setFieldValue('district', candidates[0]);
            }
          }
        });
      }
    }
    return () => {
      isMounted = false;
    };
  }, [countries]);

  const pinCodeOptions = (values.district?.pin_codes || []).map((code) => ({
    code,
  }));
  const zipUsesDropdown = !isVn() && pinCodeOptions.length > 0;

  const renderInput = (label, name, options = {}, props = {}) => (
    <Layout.FormGroup>
      <SociollaInput
        testID={`customer-info-${name}-input`}
        isTransparent={true}
        label={label}
        name={name}
        autoComplete="off"
        isRequired={options.isRequired || false}
        multiline={options.multiline || false}
        numberOfLines={options.numberOfLines || 1}
        {...props}
      />
    </Layout.FormGroup>
  );

  const renderSelectBox = (label, name, options, extraProps = {}) => (
    <Layout.FormGroup>
      <SociollaSelectBox
        testID={`customer-info-${name}-select`}
        isTransparent={true}
        label={label}
        options={options}
        name={name}
        trackBy="_id"
        labelKey="name"
        {...extraProps}
      />
    </Layout.FormGroup>
  );

  const renderCompanyAddressTitle = () => (
    <SociollaText
      type="medium"
      style={{
        fontSize: 16,
        color: Colors.black2,
        marginTop: 20,
        marginBottom: 16,
        paddingLeft: 5,
      }}>
      Company Address
    </SociollaText>
  );

  const addressForm = () => (
    <View
      style={{
        flex: isWeb ? 1 : 0,
        flexBasis: isMobileWeb ? 'auto' : 0,
      }}>
      {renderInput('Name', 'name', { isRequired: true }, { isUppercase: true })}
      {renderCompanyAddressTitle()}
      {renderInput(
        'Street',
        'street',
        { isRequired: true, multiline: true, numberOfLines: 4 },
        { isUppercase: true },
      )}
      {renderInput(
        'Street 2',
        'street2',
        { multiline: true, numberOfLines: 4 },
        { isUppercase: true },
      )}
      {renderSelectBox('Country ID', 'country', countries.data, {
        value: country,
        editable: false,
        disabled: true,
      })}
      {renderSelectBox(isVn() ? 'City ID' : 'State ID', 'state', states, {
        onChange: onChangeState,
        isRequired: true,
        editable: formEdit.state,
      })}
      {renderSelectBox(isVn() ? 'District Area' : 'City ID', 'city', cities, {
        onChange: onChangeCity,
        editable: formEdit.city,
        disabled: !formEdit.city,
        isRequired: true,
      })}
      {!isVn() &&
        renderSelectBox('District', 'district', districts, {
          onChange: onChangeDistrict,
          editable: formEdit.district,
          disabled: !formEdit.district,
          isRequired: true,
        })}
      {zipUsesDropdown ? (
        <Layout.FormGroup>
          <SociollaSelectBox
            testID="customer-info-postal_code-select"
            isTransparent={true}
            label="ZIP Code"
            name="postal_code"
            options={pinCodeOptions}
            trackBy="code"
            labelKey="code"
            isRequired
            value={values.postal_code ? { code: values.postal_code } : ''}
            onChange={(val) => setFieldValue('postal_code', val?.code || '')}
          />
        </Layout.FormGroup>
      ) : (
        renderInput(
          'ZIP Code',
          'postal_code',
          { isRequired: !isVn() },
          {
            isNumeric: true,
            keyboardType: 'numeric',
          },
        )
      )}
    </View>
  );

  return (
    <View testID="customer-info-container">
      <Layout.FormGroup style={{ marginBottom: 40 }}>
        <SociollaFileImageList
          testID="customer-info-image-upload"
          label="Upload Photo"
          placeholderImage={require('@assets/img/icons/add-image-grey.svg')}
          name="image"
          isRequired
        />
      </Layout.FormGroup>
      <View
        style={[styles.row, isWeb ? {} : { flexDirection: 'column', flex: 1 }]}>
        {addressForm()}
        {isWeb && <View style={styles.separator} />}
        <View
          style={[
            { flex: isWeb ? 1 : 0 },
            isMobileWeb && { flexBasis: 'auto' },
          ]}>
          <Layout.FormGroup>
            <SociollaSelectBox
              testID="customer-info-is_pkp-select"
              isTransparent={true}
              label="Taxable Entrepreneur"
              options={PKP_TYPES}
              name="is_pkp"
              trackBy="value"
              labelKey="name"
              isRequired
              value={{
                name: values['is_pkp']?.value ? 'Taxable' : 'Non Taxable',
                value: values['is_pkp']?.value ? true : false,
              }}
              onChange={(val) => setFieldValue('is_pkp', val)}
            />
          </Layout.FormGroup>

          <Layout.FormGroup>
            <SociollaSelectBox
              testID="customer-info-wht-select"
              isTransparent={true}
              label="WHT (Corporate/Personal)"
              options={WHT}
              name="wht"
              trackBy="value"
              labelKey="label"
              isRequired
              value={values.wht ? WHT.find((el) => el.value == values.wht) : ''}
              onChange={(val) => setFieldValue('wht', val.value)}
              editable={true}
            />
          </Layout.FormGroup>

          {renderInput('Phone', 'phone', {
            keyboardType: 'numeric',
            isNumeric: true,
          })}
          {renderInput('Mobile', 'mobile', {
            keyboardType: 'numeric',
            isNumeric: true,
          })}
          {renderInput('Email', 'email')}
          {renderInput(
            'Person in Charge (PIC) Name',
            'owner_name',
            {},
            { isUppercase: true },
          )}
          {renderInput('Job Title', 'job_position', {}, { isUppercase: true })}

          <Layout.FormGroup>
            <SociollaSelectBox
              testID="customer-info-payment_type-select"
              isTransparent={true}
              label="Payment Type"
              options={PAYMENT_TYPES}
              name="payment_type"
              trackBy="value"
              labelKey="name"
            />
          </Layout.FormGroup>

          {renderInput('Notes', 'notes', {}, { isUppercase: true })}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    width: '100%',
  },
  separator: {
    height: '100%',
    width: 1,
    backgroundColor: Colors.grey9,
    marginHorizontal: 30,
  },
});

CustomerInfo.propTypes = propTypes;
export default CustomerInfo;
